import { SubscriptionTier } from '@heidi/schema/ml-scribe';
import { useAtomValue } from 'jotai';
import { useState } from 'react';
import {
  pricingPlansHighlightedPlanAtom,
  type PricingPlansHighlightedPlan,
} from '../../billingAtoms';
import type { PlanSeat } from './plan-seat-tabs';
import type { UsePricingPlansModalStateReturn } from './usePricingPlansModalState';

type ColumnInputs = Pick<
  UsePricingPlansModalStateReturn,
  | 'currentPlan'
  | 'isClinicianStandardPlan'
  | 'isClinicianMaxSeat'
  | 'isPracticeStandardPlan'
  | 'isPracticeMaxSeat'
  | 'offersPracticeMax'
  | 'isTrialingClinician'
> & {
  /**
   * Trial-end dual-seat layout: Free/Clinician Standard see both cards;
   * Practice / Scribe Team / Clinician Max see Practice only.
   */
  layout?: 'legacy' | 'trialEnd';
};

/**
 * Which columns the pricing plans modal shows, which seat the Practice column
 * is on, and which card carries the highlight.
 *
 * The legacy modal sells upgrades, so a plan the user already pays for is
 * dropped rather than shown as "Current". A trialed plan stays — its CTA is
 * the only trial-to-paid conversion path.
 *
 * With Practice Max on sale the columns are the viewer's valid upgrades
 * (MONEY-1128): Free/trial sees all three; Clinician Standard loses Clinician;
 * Clinician Max and Practice Standard see the Practice column with its Max
 * seat only; Practice Max sees nothing (its entries are hidden upstream).
 * Before that, only the viewer's own card drops — the pre-launch behaviour
 * GRO-4552 papered over by hiding the Clinician Max entry.
 *
 * `layout: 'trialEnd'` keeps Clinician + Practice for individual viewers so
 * they can compare seats, and Practice only for team upgrades (Practice /
 * Scribe Team / Clinician Max → rising-tide Practice).
 */
export function usePricingPlanColumns({
  currentPlan,
  isClinicianStandardPlan,
  isClinicianMaxSeat,
  isPracticeStandardPlan,
  isPracticeMaxSeat,
  offersPracticeMax,
  isTrialingClinician,
  layout = 'legacy',
}: ColumnInputs) {
  const isOnPaidPlan = (isPlan: boolean) => isPlan && !isTrialingClinician;
  const isPaidClinicianStandard = isOnPaidPlan(isClinicianStandardPlan);
  const isPaidClinicianMax = isOnPaidPlan(isClinicianMaxSeat);
  const isPaidPracticeStandard = isOnPaidPlan(isPracticeStandardPlan);
  const isPaidPracticeMax = isOnPaidPlan(isPracticeMaxSeat);
  const isPaidHeidi2Plan =
    isPaidClinicianStandard ||
    isPaidClinicianMax ||
    isPaidPracticeStandard ||
    isPaidPracticeMax;
  const isPaidPracticeOrClinicianMax =
    isPaidClinicianMax || isPaidPracticeStandard || isPaidPracticeMax;
  const isTeamPlanViewer =
    isClinicianMaxSeat ||
    currentPlan === SubscriptionTier.CLINICIAN_PRO_PLUS ||
    currentPlan === SubscriptionTier.PRO_PLUS;

  const isTrialEndLayout = layout === 'trialEnd';
  const showClinician = isTrialEndLayout
    ? !isTeamPlanViewer
    : offersPracticeMax
      ? !isPaidHeidi2Plan
      : !isPaidClinicianStandard;
  const showClinicianMax = isTrialEndLayout
    ? false
    : offersPracticeMax
      ? !isPaidPracticeOrClinicianMax
      : !isPaidClinicianMax;
  const offersPracticeStandardSeat = isTrialEndLayout
    ? true
    : offersPracticeMax
      ? !isPaidPracticeOrClinicianMax
      : !(isPaidPracticeStandard || isPaidPracticeMax);
  // Trial-end still lists Max prices on the Practice card when the product exists.
  const offersPracticeMaxSeat = isTrialEndLayout
    ? offersPracticeMax
    : offersPracticeMax && !isPaidPracticeMax;
  const showPractice = isTrialEndLayout
    ? true
    : offersPracticeStandardSeat || offersPracticeMaxSeat;
  const showSeatToggle = offersPracticeStandardSeat && offersPracticeMaxSeat;
  const visibleCardCount = [showClinician, showClinicianMax, showPractice].filter(
    Boolean
  ).length;

  /**
   * The tier-toned highlight marks the card to steer towards: the one marketing
   * asked for, else Clinician. A requested card the user already pays for is
   * dropped, so the highlight falls back to Clinician — or to Clinician Max,
   * the next step up, when Clinician is the dropped card — and a lone Practice
   * column carries it itself.
   */
  const requestedPlan = useAtomValue(pricingPlansHighlightedPlanAtom) ?? 'clinician';
  // Marketing's `seat_type=MAX` on a Practice payment lands on the Max seat.
  const [selectedPracticeSeat, setSelectedPracticeSeat] = useState<PlanSeat>(
    requestedPlan === 'practiceMax' ? 'max' : 'standard'
  );
  const practiceSeat: PlanSeat = showSeatToggle
    ? selectedPracticeSeat
    : offersPracticeMaxSeat
      ? 'max'
      : 'standard';
  const isPracticeMaxSelected = practiceSeat === 'max';
  const isPlanShown: Record<PricingPlansHighlightedPlan, boolean> = {
    clinician: showClinician,
    max: showClinicianMax,
    practice: offersPracticeStandardSeat,
    practiceMax: offersPracticeMaxSeat,
  };
  // A Practice Max request with no Max seat on sale still meant Practice.
  const resolvedRequest: PricingPlansHighlightedPlan =
    requestedPlan === 'practiceMax' && !isPlanShown.practiceMax && isPlanShown.practice
      ? 'practice'
      : requestedPlan;
  const highlightedPlan: PricingPlansHighlightedPlan = isPlanShown[resolvedRequest]
    ? resolvedRequest
    : showClinician
      ? 'clinician'
      : showClinicianMax
        ? 'max'
        : isPracticeMaxSelected
          ? 'practiceMax'
          : 'practice';

  return {
    showClinician,
    showClinicianMax,
    showPractice,
    showSeatToggle,
    visibleCardCount,
    selectedPracticeSeat,
    setSelectedPracticeSeat,
    isPracticeMaxSelected,
    isClinicianHighlighted: highlightedPlan === 'clinician',
    isClinicianMaxHighlighted: highlightedPlan === 'max',
    isPracticeHighlighted:
      highlightedPlan === 'practice' || highlightedPlan === 'practiceMax',
  };
}
